import React from 'react'
import moment from 'moment'
// @src imports
import { Details, MoreInfo, SubDate } from 'src/styled'
import { Button } from 'src/chrome'
import { formatCost, sortedSubscriptionFeatures } from 'src/helpers'
import {
  NormalizedSubscriptionFeature,
  SubscriptionType,
} from 'src/user/fragments'
import { StripeSubscription } from 'src/graphql'
import { Feature } from 'src/dashboard/components'
import {
  ConfirmDialog,
  Flex,
  HStack,
  Text,
} from '@sendoutcards/quantum-design-ui'
import { useActions, useMutations, useState } from 'src/hooks'

interface Props {
  subscription: SubscriptionType | StripeSubscription
  shouldShowDates: boolean
  shouldShowPrice?: boolean
  onChangeSubscription?: () => void
  isChangingSubscription?: boolean
}

const Subscription: React.FC<Props> = props => {
  const {
    subscription: {
      id,
      name,
      price,
      startDate,
      currentPeriodEnd,
      isSetToCancel,
      features,
    },
    shouldShowDates,
    shouldShowPrice = true,
    onChangeSubscription,
    isChangingSubscription,
  } = props
  const actions = useActions()
  const mutations = useMutations()

  const [
    isShowingCancellationDialog,
    setIsShowingCancellationDialog,
  ] = useState(false)

  const isSystemPackage = name.includes('System')

  const cancelSubscription = async () => {
    const {
      cancelStripeSubscription: { account },
    } = await mutations.cancelStripeSubscription({ id })
    actions.updatedAccount(account)
  }

  return (
    <>
      <Flex justifyContent="space-between">
        <Text type="caption" color="primaryHeading" weight="bold">
          {name}
          {name === 'Enterprise Grandfathered' ? (
            <MoreInfo
              href={
                'https://www.sendoutcards.com/resources/enterprise-subscription-details/'
              }
              target="_blank"
            >
              More Information
            </MoreInfo>
          ) : null}
        </Text>
        {shouldShowPrice && <Text type="caption">{formatCost(price)}</Text>}
      </Flex>
      {shouldShowDates && (
        <Details>
          {startDate && currentPeriodEnd && (
            <>
              <SubDate>
                <Text type="caption" color="primaryHeading" weight="bold">
                  Created
                </Text>
                <Text type="caption">
                  {moment(startDate).format('MM/DD/YYYY')}
                </Text>
              </SubDate>
              <SubDate style={isSetToCancel ? { color: 'red' } : {}}>
                <Text
                  type="caption"
                  color={isSetToCancel ? 'danger' : 'primaryHeading'}
                  weight="bold"
                >
                  {isSystemPackage ? 'Expires' : 'Renewal Date'}
                </Text>
                <Text
                  type="caption"
                  color={isSetToCancel ? 'danger' : 'primaryHeading'}
                >
                  {`${
                    isSetToCancel && !isSystemPackage ? 'Will cancel on ' : ''
                  }${moment(currentPeriodEnd).format('MM/DD/YYYY')}`}
                </Text>
              </SubDate>
              <HStack gap="x1" justify="flex-end">
                {!isSystemPackage &&
                  !!onChangeSubscription &&
                  isSetToCancel && (
                    <Button
                      id={'renew_subscription_btn'}
                      style={{ marginLeft: 'auto' }}
                      title={'Renew'}
                      onClick={onChangeSubscription}
                      disabled={isChangingSubscription}
                    />
                  )}
                <Button
                  id={'cancel_subscription_btn'}
                  style={{ marginLeft: 'auto' }}
                  title={'Cancel'}
                  onClick={() => setIsShowingCancellationDialog(true)}
                  disabled={isChangingSubscription}
                />
              </HStack>
            </>
          )}
        </Details>
      )}
      {features &&
        sortedSubscriptionFeatures(features)
          .filter(f => f !== null)
          .map(key => (
            <Feature
              key={key}
              feature={features[key] as NormalizedSubscriptionFeature}
            />
          ))}
      <ConfirmDialog
        isOpen={isShowingCancellationDialog}
        title="Cancel Subscription"
        description="Are you sure you want to cancel this subscription?"
        decline={{
          title: 'Keep Subscription',
          onClick: () => setIsShowingCancellationDialog(false),
        }}
        accept={{
          title: 'Cancel Subscription',
          onClick: () =>
            cancelSubscription() && setIsShowingCancellationDialog(false),
        }}
        zIndex={1}
      />
    </>
  )
}

export default Subscription
